import React, { useState, useEffect } from "react";
import { X, Upload } from "lucide-react";
import { dataClient as base44 } from "@/services/data";
import { useTheme } from "../ThemeProvider";
import { getThemeColor, getThemeColorRaw, setThemeColor, getCategoryColor } from "./seliseColors";
import Avatar from "./Avatar";
import CLevelAssign from "./CLevelAssign";
import CollaboratorAssign from "./CollaboratorAssign";
import ImageCropper from "./ImageCropper";
import LinksEditor from "./LinksEditor";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

export default function NodeEditModal({ node, onSave, onClose, saving, cSuiteNodes, allPeople }) {
  const [formData, setFormData] = useState(node);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const { theme } = useTheme();

  useEffect(() => {
    setFormData(node);
  }, [node]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const update = (field, value) => setFormData({ ...formData, [field]: value });

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile(file);
  };

  const handleCropComplete = async (croppedFile) => {
    setPendingFile(null);
    setUploadError("");
    setUploading(true);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file: croppedFile });
      if (!file_url) throw new Error("Upload did not return a file URL");
      update("photo_url", file_url);
    } catch (err) {
      console.error("Upload failed", err);
      setUploadError(err?.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const isPerson = node.node_type === "person";
  const isDivision = node.node_type === "division";
  const isBoardOrDelegate = node.node_type === "board" || node.node_type === "delegate";
  const isCSuite = node.node_type === "c_suite";
  const showPhoto = isPerson || isBoardOrDelegate || isCSuite;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="font-bold text-gray-900 capitalize">
            {node.id ? "Edit" : "Add"}{" "}
            {node.node_type === "c_suite"
              ? "C-Suite"
              : node.node_type === "board"
              ? "Board Member"
              : node.node_type === "division"
              ? "Business Unit"
              : node.node_type}
          </h3>
          <button onClick={onClose} title="Close" className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div>
            <label className="text-sm font-medium text-gray-700">Name *</label>
            <input
              type="text"
              value={formData.name || ""}
              onChange={(e) => update("name", e.target.value)}
              required
              className="w-full mt-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent bg-card text-foreground"
            />
          </div>

          {(isPerson || isBoardOrDelegate || isCSuite) && (
            <label className="flex items-center gap-2 p-2.5 rounded-lg bg-blue-50 border border-blue-200 cursor-pointer">
              <input
                type="checkbox"
                checked={!!formData.is_vacancy}
                onChange={(e) => update("is_vacancy", e.target.checked)}
                className="w-4 h-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-blue-700">Mark as open / vacancy position</span>
            </label>
          )}

          {isBoardOrDelegate && (
            <div>
              <label className="text-sm font-medium text-gray-700">Title</label>
              <input
                type="text"
                value={formData.title || ""}
                onChange={(e) => update("title", e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent bg-card text-foreground"
              />
            </div>
          )}

          {isCSuite && (
            <>
              <div>
                <label className="text-sm font-medium text-gray-700">Title (CPO, CEO, CCO)</label>
                <input
                  type="text"
                  value={formData.title || ""}
                  onChange={(e) => update("title", e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent bg-card text-foreground"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Full Title (Chief Executive Officer)</label>
                <input
                  type="text"
                  value={formData.full_title || ""}
                  onChange={(e) => update("full_title", e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent bg-card text-foreground"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 capitalize">Color ({theme} theme)</label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    value={getThemeColor(formData.color, theme, "#1e40af")}
                    onChange={(e) => update("color", setThemeColor(formData.color, theme, e.target.value))}
                    className="w-10 h-10 rounded-lg border border-border cursor-pointer p-1 bg-card"
                  />
                  <input
                    type="text"
                    value={getThemeColorRaw(formData.color, theme)}
                    onChange={(e) => update("color", setThemeColor(formData.color, theme, e.target.value))}
                    placeholder="Auto"
                    className="flex-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent bg-card text-foreground"
                  />
                </div>
              </div>
            </>
          )}

          {isPerson && (
            <>
              <div>
                <label className="text-sm font-medium text-gray-700">Function Area</label>
                <input
                  type="text"
                  value={formData.function_area || ""}
                  onChange={(e) => update("function_area", e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent bg-card text-foreground"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Role Level</label>
                <Select
                  value={formData.role_level || "__none__"}
                  onValueChange={(v) => update("role_level", v === "__none__" ? "" : v)}
                >
                  <SelectTrigger className="w-full mt-1 h-10 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-ring">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Select...</SelectItem>
                    <SelectItem value="VP">Vice President</SelectItem>
                    <SelectItem value="Director">Director</SelectItem>
                    <SelectItem value="Head">Head</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Seniority (optional)</label>
                <select
                  value={formData.seniority || ""}
                  onChange={(e) => update("seniority", e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent bg-card text-foreground"
                >
                  <option value="">None</option>
                  <option value="Executive">Executive</option>
                  <option value="Principal">Principal</option>
                  <option value="Senior">Senior</option>
                  <option value="Deputy">Deputy</option>
                </select>
              </div>
            </>
          )}

          {isPerson && cSuiteNodes && cSuiteNodes.length > 0 && (
            <div>
              <label className="text-sm font-medium text-gray-700">C-Level</label>
              <div className="flex items-center gap-3 mt-1 p-2 rounded-lg bg-gray-50">
                <Avatar name={formData.name || "?"} photoUrl={formData.photo_url} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{formData.name || "—"}</p>
                  <p className="text-xs text-gray-500 truncate">{formData.function_area}</p>
                </div>
                <CLevelAssign
                  cSuiteNodes={cSuiteNodes}
                  assignedCLevelId={formData.c_level_id}
                  onAssign={(cLevelId) => update("c_level_id", cLevelId)}
                  isAdmin={true}
                />
              </div>
            </div>
          )}

          {isPerson && allPeople && allPeople.length > 0 && (
            <CollaboratorAssign
              allPeople={allPeople}
              collaboratorIds={formData.collaborators || []}
              excludeId={formData.id}
              onAssign={(ids) => update("collaborators", ids)}
            />
          )}

          {showPhoto && (
            <div>
              <label className="text-sm font-medium text-gray-700">Photo</label>
              <div className="flex items-center gap-3 mt-1">
                {formData.photo_url && (
                  <img
                    src={formData.photo_url}
                    alt="preview"
                    className="w-12 h-12 rounded-full object-cover border border-gray-200"
                  />
                )}
                <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer">
                  <Upload className="w-4 h-4" />
                  {uploading ? "Uploading..." : formData.photo_url ? "Change" : "Upload"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoSelect}
                    disabled={uploading}
                    className="hidden"
                  />
                </label>
                {formData.photo_url && (
                  <button
                    type="button"
                    onClick={() => update("photo_url", "")}
                    className="text-sm text-red-500 hover:text-red-700"
                  >
                    Remove
                  </button>
                )}
              </div>
              {uploadError && (
                <p className="mt-1 text-xs text-red-600" role="alert">{uploadError}</p>
              )}
            </div>
            )}

          {showPhoto && (
            <LinksEditor
              links={formData.links || []}
              onChange={(links) => update("links", links)}
            />
          )}

          {isDivision && (
            <>
              <div>
                <label className="text-sm font-medium text-gray-700">Business Unit Name</label>
                <input
                  type="text"
                  value={formData.department || ""}
                  onChange={(e) => update("department", e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent bg-card text-foreground"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Business Unit Code</label>
                <input
                  type="text"
                  value={formData.department_code || ""}
                  onChange={(e) => update("department_code", e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent bg-card text-foreground"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Category</label>
                <select
                  value={formData.category || ""}
                  onChange={(e) => update("category", e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent bg-card text-foreground"
                >
                  <option value="industry">Industry</option>
                  <option value="product">Product</option>
                  <option value="service">Service</option>
                  <option value="support">Support</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Unit Size</label>
                <select
                  value={formData.unit_size || "N/A"}
                  onChange={(e) => update("unit_size", e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent bg-card text-foreground"
                >
                  <option value="N/A">N/A</option>
                  {["0-15","15-30","30-45","45-60","60-75","75-90","90-105","105-120","120-135","135-150","150-165","165-180","180-195","195+"].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 capitalize">Category Color ({theme} theme)</label>
                <p className="text-xs text-gray-400 mb-1">Applies to all units in this category.</p>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={getThemeColor(formData.color, theme, getCategoryColor(formData.category))}
                    onChange={(e) => update("color", setThemeColor(formData.color, theme, e.target.value))}
                    className="w-10 h-10 rounded-lg border border-border cursor-pointer p-1 bg-card"
                  />
                  <input
                    type="text"
                    value={getThemeColorRaw(formData.color, theme)}
                    onChange={(e) => update("color", setThemeColor(formData.color, theme, e.target.value))}
                    placeholder="Auto"
                    className="flex-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent bg-card text-foreground"
                  />
                </div>
              </div>

            </>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
      {pendingFile && (
        <ImageCropper
          file={pendingFile}
          onCancel={() => setPendingFile(null)}
          onCrop={handleCropComplete}
        />
      )}
    </div>
  );
}